import React, { useMemo, useState } from "react";
import ConversionRatesChart from "@/components/graphs/ConversionRates/ConversionRatesChart";
import WidgetCard from "@/components/controls/WidgetCard";
import { useTranslation } from "react-i18next";
import Tabs from "@/components/controls/Tabs";
import Dropdown from "@/components/controls/Dropdown/Dropdown";
import { Trophy } from "@dreamhub/designsystem/icons";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/utils/utils";
import { UserData } from "@/entities/users/types";
import ComparisonChart from "@/components/graphs/ComparisonChart/ComparisonChart";
import Text from "@/components/ui/text";
import ConversionDrilldownModal from "@/components/layouts/ConversionDrilldownModal/ConversionDrilldownModal";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { DealMilestone } from "@/pages/deals/types";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import {
  ConversionRatesDirectReportsResponse,
  SettingsPeriod
} from "@/entities/reporting/types";
import {
  periodEnumToString,
  periodStringToEnum
} from "@/entities/reporting/helpers";
import {
  percentageComparisonChartLabels,
  periodsDropdownProps,
  renderMilestone
} from "./helpers";
import { ConversionBreadcrumbsProvider } from "@/components/layouts/ConversionRates/ConversionBreadcrumbsContext";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { find, groupBy, isEmpty, map } from "lodash";

export enum ConversionRatesChartTabType {
  STAGES = "stages",
  MILESTONES = "milestones"
}

export type ConversionRatesChartDataType = {
  id: string;
  value: number;
  label: string;
  color?: string;
  percentageToWin: number;
  milestone: DealMilestone;
  team?: number;
  org?: number;
  industry?: number;
};

export type ConversionRatesChartData = {
  [key in ConversionRatesChartTabType]: ConversionRatesChartDataType[];
};

interface ConversionRatesPropsType {
  chartData: ConversionRatesChartData;
  userData?: UserData;
  period: SettingsPeriod;
  periodSelect: (period: SettingsPeriod) => void;
  isLoading?: boolean;
  isAE: boolean;
  drillDownHidden?: boolean;
  directReportsData?: ConversionRatesDirectReportsResponse;
}

const ConversionRates: React.FC<ConversionRatesPropsType> = ({
  chartData,
  userData,
  period,
  periodSelect,
  isLoading,
  isAE,
  drillDownHidden = false,
  directReportsData
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(
    ConversionRatesChartTabType.STAGES
  );
  const [selectedStageId, setSelectedStageId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isDropdownClosing } = useDropdownClosing();
  const showIndustryValue = useFeatureFlag(FeatureFlags.INDUSTRY_VALUE).enabled;

  const renderLabel = () => {
    const selectedElement = find(
      chartData[selectedTab],
      (element) => element.id === selectedStageId
    );
    return selectedElement ? selectedElement.label : "";
  };

  const renderPercentage = (property: "value" | "percentageToWin") => {
    const selectedElement = find(
      chartData[selectedTab],
      (element) => element.id === selectedStageId
    );
    return selectedElement ? `${selectedElement[property]}%` : "";
  };

  const percentageComparisonLabels = useMemo(
    () => percentageComparisonChartLabels(t),
    [t]
  );

  const getComparisonChartDatasets = () => {
    const selectedElement = find(
      chartData[selectedTab],
      (element) => element.id === selectedStageId
    );
    const chartPercentages = [
      selectedElement?.value || 0,
      selectedElement?.team || 0,
      selectedElement?.org || 0,
      selectedElement?.industry || 0
    ];
    return map(chartPercentages, (percentage, index) => ({
      name: percentageComparisonLabels[index],
      value: percentage
    }));
  };

  const renderStageInfo = () => {
    return selectedStageId === "" ? (
      <div className="h-full flex justify-center items-center">
        <Text
          as="div"
          variant="bodySecondary"
          className="text-wrap text-center"
        >{`${t("stageAverageDuration.clickMessageFirst")} ${t(`stageAverageDuration.${selectedTab === ConversionRatesChartTabType.STAGES ? "stage" : "milestone"}`)} ${t("stageAverageDuration.clickMessageSecond")}`}</Text>
      </div>
    ) : (
      <>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3">
            <Text
              as="div"
              data-test={`${selectedStageId}-label`}
              variant="lgSubtitleH3"
            >
              {renderLabel()}
            </Text>
            <Text
              as="div"
              data-test={`${selectedStageId}-percentage`}
              variant="title3H1"
              className="flex gap-2"
            >
              {renderPercentage("value")}
              <Text
                as="span"
                variant="bodySecondary"
                className="self-end"
              >{`${selectedTab === ConversionRatesChartTabType.STAGES ? t("conversionRates.stage") : t("conversionRates.milestone")} ${t("conversionRates.conversion")}`}</Text>
            </Text>
            <Text as="div" variant="bodySecondary" className="flex gap-1">
              {"("}
              <Text
                data-test={`${selectedStageId}-percentage-to-win`}
                as="div"
                variant="label"
                className="flex gap-2"
              >
                {renderPercentage("percentageToWin")}
              </Text>
              {`${t("conversionRates.winConversion")} )`}
            </Text>
          </div>
        </div>
        <div className="max-h-52 h-full pb-14">
          <ComparisonChart
            unit="%"
            hideIndustry={
              !showIndustryValue ||
              selectedTab === ConversionRatesChartTabType.STAGES
            }
            userData={userData}
            datasets={getComparisonChartDatasets()}
          />
        </div>
      </>
    );
  };

  const stageGroups = !isEmpty(chartData)
    ? groupBy(chartData[ConversionRatesChartTabType.STAGES], "milestone")
    : {};

  const handleClick = (id: string) => {
    setSelectedStageId(id !== selectedStageId ? id : "");
  };

  const renderStage = (stage: ConversionRatesChartDataType, index: number) => (
    <div className="flex gap-2 grow items-center" key={index}>
      <Avatar className="!size-5 !rounded-lg !text-[10px] !text-on-surface">
        <AvatarFallback
          style={{
            backgroundColor: getColorForMilestoneByType(
              stage.milestone || undefined
            )
          }}
        >
          {index + 1}
        </AvatarFallback>
      </Avatar>
      <Text as="div" variant="bodySecondary">
        {stage.label}
      </Text>
    </div>
  );

  return (
    <>
      <WidgetCard
        cardHeight="29rem"
        title={t("conversionRates.title")}
        isLoading={isLoading}
        feedBackButtonIdContext="conversion-rates"
        infoTooltip={t("conversionRates.info")}
        isDrillDownHidden={isAE || drillDownHidden}
        drillDownOnClick={() => setIsModalOpen(!isModalOpen)}
        tabs={
          <Tabs
            options={{
              [ConversionRatesChartTabType.STAGES]: t(
                "stageAverageDuration.stages"
              ),
              [ConversionRatesChartTabType.MILESTONES]: t(
                "stageAverageDuration.milestones"
              )
            }}
            defaultValue={ConversionRatesChartTabType.STAGES}
            valueSelected={(value) => {
              setSelectedStageId("");
              setSelectedTab(value as ConversionRatesChartTabType);
            }}
          />
        }
        filter={
          <Dropdown
            {...periodsDropdownProps(t)}
            defaultValue={periodEnumToString(period)}
            valueSelected={(value) => periodSelect(periodStringToEnum(value))}
          />
        }
      >
        <div
          className={`transition-opacity duration-300 w-full h-full ${chartData ? "opacity-100" : "opacity-0"}`}
        >
          <div className="relative flex w-full h-full">
            <div className="grow flex justify-center pl-[10rem]">
              <div className="relative flex flex-col gap-6 h-full w-[27rem]">
                <div className="relative w-full h-full">
                  {[
                    ConversionRatesChartTabType.STAGES,
                    ConversionRatesChartTabType.MILESTONES
                  ].map((tab) => (
                    <div
                      key={tab}
                      className={cn(
                        "absolute inset-0 transition-opacity duration-750",
                        selectedTab === tab
                          ? "opacity-100"
                          : "opacity-0 pointer-events-none"
                      )}
                    >
                      <ConversionRatesChart
                        data={chartData[tab]}
                        selectedStageId={selectedStageId}
                      />
                    </div>
                  ))}
                </div>
                <Text
                  as="div"
                  variant="label"
                  className="absolute inset-0 flex flex-col w-full h-[85%] justify-center items-center"
                >
                  {map(chartData[selectedTab], (element, index) => (
                    <div
                      key={index}
                      className="grow flex flex-col justify-end translate-y-2"
                    >
                      <div
                        data-test={`${element.id}-percentage-to-next`}
                        className={cn(
                          "h-fit w-fit p-[2px] rounded-md transition-colors duration-200",
                          selectedStageId === element.id
                            ? "bg-h2 text-disabled"
                            : "bg-[#0C111D4D] text-primary"
                        )}
                      >{`${element.value}%`}</div>
                    </div>
                  ))}
                </Text>
                <div className="justify-center flex gap-1 text-h3 items-center">
                  <span className="text-lg -translate-y-1">
                    <Trophy />
                  </span>
                  <Text as="div" variant="bodySecondary">
                    {t("conversionRates.dealWon")}
                  </Text>
                </div>
              </div>
              <div className="absolute inset-0 w-[75%] h-[85%] flex flex-col">
                {map(chartData[selectedTab], (element, index) => (
                  <div
                    key={index}
                    onClick={() => handleClick(element.id)}
                    data-test={`${element.id}-element`}
                    className="relative grow flex items-center transition-colors duration-200 hover:bg-slate-950/20 cursor-pointer"
                  >
                    <svg className="absolute inset-0 w-full h-full pointer-events-none">
                      <line
                        x1="0"
                        y1="0"
                        x2="100%"
                        y2="0"
                        stroke="var(--widget)"
                        strokeWidth="1"
                        strokeDasharray="5,5"
                      />
                    </svg>
                    {selectedTab === ConversionRatesChartTabType.STAGES
                      ? renderStage(element, index)
                      : renderMilestone(t, stageGroups, element, index)}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col justify-between w-[12.5rem] min-w-10">
              {renderStageInfo()}
            </div>
          </div>
        </div>
      </WidgetCard>
      <ConversionBreadcrumbsProvider
        leadUser={userData}
        period={period}
        enableDirectReportsData={isModalOpen}
        initialDirectReportsData={directReportsData}
      >
        <ConversionDrilldownModal
          isOpen={isModalOpen && !isDropdownClosing}
          setIsOpen={setIsModalOpen}
          chartData={chartData}
          mainSelectedStageId={selectedStageId}
          mainSelectedTab={selectedTab}
          period={period}
          periodSelect={periodSelect}
        />
      </ConversionBreadcrumbsProvider>
    </>
  );
};

export default ConversionRates;
